/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { TOAST_TYPE_DANGER, TOAST_TYPE_LOADING, TOAST_TYPE_SUCCESS } from '@squiz/sds';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { toastFactory } from '../../../../../Services/file-store';
import { GetFileStoreMappingListResponse, GetFileStoreMappingResponse } from '../../FileStoreMappings.service.defs';
import {
  CreateFileStoreMappingDto,
  GetFileStoreMappingDto,
  GetFileStoreMappingListDto,
  UpdateFileStoreMappingDto,
} from '../../FileStoreMappings.service.dto';
import { getFileStoreMappingsService } from '../../Utilities';
import { USE_FILE_STORE_MAPPINGS_STRINGS } from './useFileStoreMappings.constants';
import { useFileStoreMappingsReturnType } from './useFileStoreMappings.defs';

export const useFileStoreMappings = (): useFileStoreMappingsReturnType => {
  const { updateActiveToast } = useToastStore();
  const fileStoreMappingsService = getFileStoreMappingsService();
  const { t } = useTranslation();
  const labels = {
    creatingMapping: t(USE_FILE_STORE_MAPPINGS_STRINGS.creatingMapping),
    deletingMapping: t(USE_FILE_STORE_MAPPINGS_STRINGS.deletingMapping),
    deletingMappingSuccessful: t(USE_FILE_STORE_MAPPINGS_STRINGS.deletingMappingSuccessful),
    error: t(USE_FILE_STORE_MAPPINGS_STRINGS.error),
    mappingCreationSuccessful: t(USE_FILE_STORE_MAPPINGS_STRINGS.mappingCreationSuccessful),
    updatingMapping: t(USE_FILE_STORE_MAPPINGS_STRINGS.updatingMapping),
    updatingMappingSuccessful: t(USE_FILE_STORE_MAPPINGS_STRINGS.updatingMappingSuccessful),
  };

  const createMapping = async (dto: CreateFileStoreMappingDto): Promise<void> => {
    const loadingToast = toastFactory({ toastContent: <>{labels.creatingMapping}</>, type: TOAST_TYPE_LOADING });
    const successToast = toastFactory({
      toastContent: <>{labels.mappingCreationSuccessful}</>,
      type: TOAST_TYPE_SUCCESS,
    });
    const genericErrorToast = toastFactory({ toastContent: <>{labels.error}</>, type: TOAST_TYPE_DANGER });

    updateActiveToast(loadingToast);
    return await lastValueFrom(fileStoreMappingsService.createMapping(dto))
      .then(() => {
        return updateActiveToast(successToast);
      })
      .catch((err) => {
        const errorMessage = err.response?.message;
        if (errorMessage) {
          const descriptiveErrorToast = toastFactory({ toastContent: <>{errorMessage}</>, type: TOAST_TYPE_DANGER });
          return updateActiveToast(descriptiveErrorToast);
        }

        return updateActiveToast(genericErrorToast);
      });
  };

  const getMappingList = async (dto: GetFileStoreMappingListDto): Promise<GetFileStoreMappingListResponse | null> => {
    const errorToast = toastFactory({ toastContent: <>{labels.error}</>, type: TOAST_TYPE_DANGER });
    return await lastValueFrom(fileStoreMappingsService.getMappingList(dto))
      .then((res) => {
        return res;
      })
      .catch(() => {
        updateActiveToast(errorToast);
        return null;
      });
  };

  const getMapping = async (dto: GetFileStoreMappingDto): Promise<GetFileStoreMappingResponse | null> => {
    return await lastValueFrom(fileStoreMappingsService.getMapping(dto))
      .then((res) => {
        return res;
      })
      .catch(() => {
        return null;
      });
  };

  const updateMapping = async (dto: UpdateFileStoreMappingDto): Promise<void> => {
    const loadingToast = toastFactory({ toastContent: <>{labels.updatingMapping}</>, type: TOAST_TYPE_LOADING });
    const successToast = toastFactory({
      toastContent: <>{labels.updatingMappingSuccessful}</>,
      type: TOAST_TYPE_SUCCESS,
    });
    const genericErrorToast = toastFactory({ toastContent: <>{labels.error}</>, type: TOAST_TYPE_DANGER });

    updateActiveToast(loadingToast);
    return await lastValueFrom(fileStoreMappingsService.updateMapping(dto))
      .then(() => {
        return updateActiveToast(successToast);
      })
      .catch((err) => {
        const errorMessage = err.response?.message;
        if (errorMessage) {
          const descriptiveErrorToast = toastFactory({ toastContent: <>{errorMessage}</>, type: TOAST_TYPE_DANGER });
          return updateActiveToast(descriptiveErrorToast);
        }

        return updateActiveToast(genericErrorToast);
      });
  };

  return { createMapping, getMapping, getMappingList, updateMapping };
};
